import { LinkOutlined } from '@ant-design/icons';
import { message, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table/Table';
import config from 'config';
import { Routes } from 'config/routes';
import { format } from 'date-fns';
import {
  LabworkOutput,
  LabworkListSort,
  LabworkStatus,
  PermissionsEnum,
  LabworkListFilterInput,
} from 'gql/types';
import { Link } from 'modules/common/components';
import { LocationFilterDropdown } from 'modules/common/components/LocationFilterDropdown';
import RestrictedLink from 'modules/common/components/RestrictedLink';
import { ISortOption } from 'modules/common/interfaces';
import { createSortOrderFactory } from 'modules/common/utils/sort/createSortOrderFactory';
import { generatePath } from 'react-router-dom';
import { getTranslation } from 'utils/getTranslation';
import { shortenTextWithEllipsis } from 'utils/shortenTextWithEllipsis';

import styles from './styles.module.scss';

const t = getTranslation('donor.GeneralLabworkTable');

export const statusFilters = Object.values(LabworkStatus).map((status) => {
  return {
    text: t(status.toLowerCase()),
    value: status,
  };
});

type ColumnsProps = {
  showSorterTooltip?: boolean;
  showFilters?: boolean;
  sort: ISortOption<LabworkListSort>;
  filter: LabworkListFilterInput;
  locationFilterOptions: { text: string; value: string }[] | undefined;
};

export const getColumns = ({
  showSorterTooltip,
  showFilters,
  sort,
  filter,
}: ColumnsProps): ColumnsType<LabworkOutput> => {
  const getDefaultSort = createSortOrderFactory<LabworkListSort>(sort);
  return [
    {
      title: t('donorId'),
      render: (row: LabworkOutput) => row.donor.shortId,
      align: 'center',
    },
    {
      title: t('petName'),
      key: LabworkListSort.PetName,
      render: ({ donor }: LabworkOutput) => (
        <RestrictedLink
          to={generatePath(Routes.DonorsProfile, {
            id: donor.shortId.toString(),
          })}
          permissions={[PermissionsEnum.ViewTheListOfDonors]}
        >
          {donor.name}
        </RestrictedLink>
      ),
      align: 'center',
      sorter: true,
      sortOrder: getDefaultSort(LabworkListSort.PetName),
      showSorterTooltip,
    },
    {
      title: t('lastName'),
      key: LabworkListSort.LastName,
      render: (row: LabworkOutput) => row.donor.user?.lastName,
      align: 'center',
      sorter: true,
      sortOrder: getDefaultSort(LabworkListSort.LastName),
      showSorterTooltip,
    },
    {
      title: t('dateOfLatestLabwork'),
      key: LabworkListSort.CollectionDate,
      render: (row: LabworkOutput) =>
        format(new Date(row.collectionDate), config.DATE_FORMATS.DATE),
      align: 'center',
      sorter: true,
      sortOrder: getDefaultSort(LabworkListSort.CollectionDate),
      showSorterTooltip,
    },
    {
      title: t('status'),
      key: 'statuses',
      render: (row: LabworkOutput) => t(row.status.toLowerCase()),
      align: 'center',
      filters: statusFilters,
      filteredValue: filter?.statuses || undefined,
    },
    {
      title: t('location'),
      key: 'locationName',
      filteredValue: filter?.locationName || null,
      filterDropdown: showFilters
        ? (props) => <LocationFilterDropdown {...props} />
        : undefined,
      render: (row: LabworkOutput) => {
        return row.donor.location ? (
          <Tooltip title={row.donor.location}>
            {shortenTextWithEllipsis(row.donor.location, 30)}
          </Tooltip>
        ) : (
          '-'
        );
      },
      align: 'center',
    },
    {
      title: t('link'),
      render: (row: LabworkOutput) => {
        const basePath = generatePath(Routes.DonorsLabworkBy, {
          id: row.donor.shortId.toString(),
        });

        const pathWithHighlight = `${basePath}?highlight=${row.id}`;
        const fullUrl = `${window.location.origin}${pathWithHighlight}`;

        const handleLinkClick = () => {
          navigator.clipboard
            .writeText(fullUrl)
            .then(() => {
              message.success(t('linkCopiedToClipboard'));
            })
            .catch(() => {
              message.error('failedToCopy');
            });
        };

        return (
          <Link to={pathWithHighlight} onClick={handleLinkClick}>
            <LinkOutlined className={styles.linkIcon} />
          </Link>
        );
      },
      align: 'center',
    },
  ];
};
